import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, ArrowRight, Flame, Check } from 'lucide-react';
import { KITS } from '../data/kits';
import { UPSELLS } from '../data/upsells';
import { useCountdown } from '../hooks/useCountdown';
interface NavbarProps {
  selectedKitIds: string[];
  selectedUpsellIds: string[];
  onRemoveUpsell: (id: string) => void;
  onRemoveKit: (id: string) => void;
  onCheckoutAttempt: () => void;
}
function BrandLogo() {
  return <div className="flex items-center gap-2.5" data-id="element-579">
      <img src="https://cdn.magicpatterns.com/uploads/qSJmtYJXdqJECu9iFvoXiT/ChatGPT_Image_20_may_2026,_11_40_53_p.m..png" alt="Recetas PowerFit" className="w-10 h-10 rounded-full object-cover flex-shrink-0" data-id="element-580" />
      <span className="text-xl font-extrabold tracking-tight leading-none" data-id="element-581">
        <span style={{
        color: '#2F5233'
      }} data-id="element-582">
          Recetas Power
        </span>
        <span style={{
        color: '#7CB342'
      }} data-id="element-583">
          Fit
        </span>
      </span>
    </div>;
}
export function Navbar({
  selectedKitIds,
  selectedUpsellIds,
  onRemoveUpsell,
  onRemoveKit,
  onCheckoutAttempt
}: NavbarProps) {
  const [cartOpen, setCartOpen] = useState(false);
  const cartRef = useRef<HTMLDivElement>(null);
  const selectedKits = KITS.filter(k => selectedKitIds.includes(k.id));
  const selectedUpsells = UPSELLS.filter(u => selectedUpsellIds.includes(u.id));
  const itemCount = selectedKits.length + selectedUpsells.length;
  const total = selectedKits.reduce((sum, k) => sum + k.price, 0) + selectedUpsells.reduce((sum, u) => sum + u.price, 0);
  const oldTotal = selectedKits.reduce((sum, k) => sum + k.oldPrice, 0) + selectedUpsells.reduce((sum, u) => sum + u.oldPrice, 0);
  // Empty-cart state: reflect the real lowest current offer price (whichever
  // product — kit or complemento — is cheapest right now) and its old price,
  // so the header always matches the prices actually shown on the page.
  const allProducts = [...KITS, ...UPSELLS];
  const cheapestProduct = allProducts.reduce((min, p) => p.price < min.price ? p : min, allProducts[0]);
  const lowestPrice = cheapestProduct.price;
  const lowestOldPrice = cheapestProduct.oldPrice;
  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (cartRef.current && !cartRef.current.contains(e.target as Node)) {
        setCartOpen(false);
      }
    }
    if (cartOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cartOpen]);
  return <motion.nav initial={{
    y: -100
  }} animate={{
    y: 0
  }} className="fixed top-10 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-ink/5 shadow-sm" data-id="element-584">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 lg:py-0 lg:h-20 grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-2 lg:gap-4" data-id="element-585">
        <BrandLogo data-id="element-586" />
        {/* Live Price Display */}
        <div className="hidden lg:flex flex-col items-end justify-center min-w-0" data-id="element-587">
          {itemCount > 0 ? <>
              <div className="flex items-center gap-2" data-id="element-588">
                <span className="text-xs text-ink-light font-medium uppercase tracking-wider" data-id="element-589">
                  Tu total
                </span>
                <span className="text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded" data-id="element-590">
                  50% OFF
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-591">
                <motion.span key={total} initial={{
              scale: 1.2,
              color: '#C9A84C'
            }} animate={{
              scale: 1,
              color: '#1A1A1A'
            }} transition={{
              duration: 0.4
            }} className="font-extrabold text-xl text-ink tabular-nums" data-id="element-592">
                  ${total.toLocaleString('es-AR')}
                </motion.span>
                <span className="text-xs text-ink-light line-through decoration-red-500/50" data-id="element-593">
                  ${oldTotal.toLocaleString('es-AR')}
                </span>
              </div>
            </> : <>
              <span className="text-xs text-ink-light line-through decoration-red-500/50 tabular-nums" data-id="element-594">
                ${lowestOldPrice.toLocaleString('es-AR')}
              </span>
              <span className="font-bold text-lg text-ink tabular-nums" data-id="element-595">
                desde ${lowestPrice.toLocaleString('es-AR')} ARS
              </span>
            </>}
        </div>
        {/* Cart Button + Popover */}
        <div className="relative justify-self-end lg:col-start-3" ref={cartRef} data-id="element-596">
          <button onClick={() => setCartOpen(o => !o)} aria-label={`Carrito con ${itemCount} ${itemCount === 1 ? 'producto' : 'productos'}`} className="relative w-12 h-12 rounded-full bg-white border border-ink/10 hover:border-combo-green hover:shadow-md transition-all flex items-center justify-center text-ink" data-id="element-597">
            <ShoppingCart className="w-5 h-5" data-id="element-598" />
            {itemCount > 0 && <motion.span key={itemCount} initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 20
          }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-cream" data-id="element-599">
                {itemCount}
              </motion.span>}
          </button>
          <AnimatePresence data-id="element-600">
            {cartOpen && <motion.div initial={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} transition={{
            duration: 0.18
          }} className="absolute right-0 mt-3 w-[calc(100vw-2rem)] max-w-[340px] sm:max-w-[380px] bg-white rounded-2xl shadow-premium-hover border border-ink/10 overflow-hidden z-50" data-id="element-601">
                {/* Header */}
                <div className="bg-ink text-white px-5 py-4 flex items-center justify-between" data-id="element-602">
                  <div className="flex items-center gap-2" data-id="element-603">
                    <ShoppingCart className="w-4 h-4" data-id="element-604" />
                    <span className="font-bold text-sm" data-id="element-605">Tu carrito</span>
                    {itemCount > 0 && <span className="text-xs text-white/60" data-id="element-606">
                        ({itemCount}{' '}
                        {itemCount === 1 ? 'producto' : 'productos'})
                      </span>}
                  </div>
                  <button onClick={() => setCartOpen(false)} aria-label="Cerrar" className="text-white/70 hover:text-white transition-colors" data-id="element-607">
                    <X className="w-4 h-4" data-id="element-608" />
                  </button>
                </div>
                {/* Body */}
                <div className="max-h-[400px] overflow-y-auto" data-id="element-609">
                  {itemCount === 0 ? <div className="px-5 py-10 text-center" data-id="element-610">
                      <div className="w-14 h-14 rounded-full bg-cream-dark mx-auto mb-4 flex items-center justify-center" data-id="element-611">
                        <ShoppingCart className="w-6 h-6 text-ink-light" data-id="element-612" />
                      </div>
                      <p className="text-sm text-ink-light mb-4" data-id="element-613">
                        Tu carrito está vacío.
                        <br data-id="element-614" />
                        Elegí tu kit para empezar.
                      </p>
                      <a href="#kits" onClick={() => setCartOpen(false)} className="inline-flex items-center gap-2 bg-combo-green text-white text-xs font-bold px-4 py-2.5 rounded-full hover:bg-combo-green/90 transition-all" data-id="element-615">
                        ELEGÍ TU KIT <ArrowRight className="w-3 h-3" data-id="element-616" />
                      </a>
                    </div> : <div className="divide-y divide-ink/5" data-id="element-617">
                      <AnimatePresence initial={false} data-id="element-618">
                        {selectedKits.map(kit => <motion.div key={kit.id} layout initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="px-5 py-3 flex items-start gap-3" data-id="element-619">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${kit.color} flex items-center justify-center text-white text-xs font-bold`} data-id="element-620">
                              KIT
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-621">
                              <div className="text-xs font-bold text-combo-green uppercase tracking-wide mb-0.5" data-id="element-622">
                                Kit principal
                              </div>
                              <div className="text-sm font-bold text-ink truncate" data-id="element-623">
                                {kit.name}
                              </div>
                              <div className="flex items-center gap-2 mt-1" data-id="element-624">
                                <span className="text-sm font-bold text-ink" data-id="element-625">
                                  ${kit.price.toLocaleString('es-AR')}
                                </span>
                                <span className="text-xs text-ink-light line-through" data-id="element-626">
                                  ${kit.oldPrice.toLocaleString('es-AR')}
                                </span>
                              </div>
                            </div>
                            <button onClick={() => onRemoveKit(kit.id)} aria-label={`Quitar ${kit.name}`} className="text-ink-light hover:text-red-600 transition-colors flex-shrink-0" data-id="element-627">
                              <X className="w-4 h-4" data-id="element-628" />
                            </button>
                          </motion.div>)}
                        {selectedUpsells.map(upsell => <motion.div key={upsell.id} layout initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="px-5 py-3 flex items-start gap-3" data-id="element-629">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{
                      backgroundColor: upsell.color
                    }} data-id="element-630">
                              +
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-631">
                              <div className="text-xs font-bold text-ink-light uppercase tracking-wide mb-0.5" data-id="element-632">
                                Complemento
                              </div>
                              <div className="text-sm font-bold text-ink truncate" data-id="element-633">
                                {upsell.name}
                              </div>
                              <div className="flex items-center gap-2 mt-1" data-id="element-634">
                                <span className="text-sm font-bold text-ink" data-id="element-635">
                                  ${upsell.price.toLocaleString('es-AR')}
                                </span>
                                <span className="text-xs text-ink-light line-through" data-id="element-636">
                                  ${upsell.oldPrice.toLocaleString('es-AR')}
                                </span>
                              </div>
                            </div>
                            <button onClick={() => onRemoveUpsell(upsell.id)} aria-label={`Quitar ${upsell.name}`} className="text-ink-light hover:text-red-600 transition-colors flex-shrink-0" data-id="element-637">
                              <X className="w-4 h-4" data-id="element-638" />
                            </button>
                          </motion.div>)}
                      </AnimatePresence>
                    </div>}
                </div>
                {/* Footer */}
                {itemCount > 0 && <div className="bg-cream-dark px-5 py-4 border-t border-ink/5" data-id="element-639">
                    <div className="flex items-center justify-between mb-1" data-id="element-640">
                      <span className="text-xs text-ink-light line-through" data-id="element-641">
                        ${oldTotal.toLocaleString('es-AR')}
                      </span>
                      <span className="text-xs font-bold text-combo-green" data-id="element-642">
                        Ahorrás ${(oldTotal - total).toLocaleString('es-AR')}
                      </span>
                    </div>
                    <div className="flex items-end justify-between mb-4" data-id="element-643">
                      <span className="text-sm font-bold text-ink uppercase tracking-wide" data-id="element-644">
                        Total
                      </span>
                      <motion.span key={total} initial={{
                  scale: 1.15,
                  color: '#C9A84C'
                }} animate={{
                  scale: 1,
                  color: '#1A1A1A'
                }} transition={{
                  duration: 0.35
                }} className="text-2xl font-extrabold tabular-nums" data-id="element-645">
                        ${total.toLocaleString('es-AR')}
                      </motion.span>
                    </div>
                    <a href="#cart" onClick={() => setCartOpen(false)} className="w-full bg-combo-green text-white text-sm font-bold py-3 rounded-full hover:bg-combo-green/90 transition-all shadow-glow flex items-center justify-center gap-2" data-id="element-646">
                      VER CARRITO Y CONFIRMAR <ArrowRight className="w-4 h-4" data-id="element-647" />
                    </a>
                  </div>}
              </motion.div>}
          </AnimatePresence>
        </div>
        <div className="col-span-2 lg:col-span-1 lg:col-start-4 w-full lg:w-auto" data-id="element-648">
          <SmartCTA itemCount={itemCount} total={total} oldTotal={oldTotal} onCheckoutAttempt={onCheckoutAttempt} data-id="element-649" />
        </div>
      </div>
    </motion.nav>;
}
interface SmartCTAProps {
  itemCount: number;
  total: number;
  oldTotal: number;
  onCheckoutAttempt: () => void;
}
function SmartCTA({
  itemCount,
  total,
  oldTotal,
  onCheckoutAttempt
}: SmartCTAProps) {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const hasItems = itemCount > 0;
  const savings = oldTotal - total;
  // With items in cart → routed through App's checkout gate (shows the
  // bundle popup the first time, then goes to Shopify on subsequent clicks).
  if (hasItems) {
    return <motion.button type="button" onClick={onCheckoutAttempt} whileHover={{
      scale: 1.03
    }} whileTap={{
      scale: 0.97
    }} animate={{
      boxShadow: ['0 0 0 0 rgba(27,67,50,0.5)', '0 0 0 12px rgba(27,67,50,0)', '0 0 0 0 rgba(27,67,50,0)']
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} className="relative group bg-combo-green text-white px-4 sm:px-6 py-2.5 rounded-full hover:bg-combo-green/90 transition-all flex items-center gap-2 sm:gap-3 min-h-[48px] shadow-premium w-full lg:w-auto justify-center lg:justify-start" data-id="element-650">
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center" data-id="element-651">
          <Check className="w-3 h-3" strokeWidth={3} data-id="element-652" />
        </div>
        <div className="flex flex-col items-start leading-tight min-w-0" data-id="element-653">
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider opacity-90" data-id="element-654">
            Finalizar compra
          </span>
          <div className="flex items-baseline gap-1.5" data-id="element-655">
            <span className="text-sm sm:text-base font-extrabold tabular-nums" data-id="element-656">
              ${total.toLocaleString('es-AR')}
            </span>
            {savings > 0 && <span className="hidden sm:inline text-[10px] font-bold text-yellow-300 tabular-nums" data-id="element-657">
                ahorrás ${savings.toLocaleString('es-AR')}
              </span>}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" data-id="element-658" />
      </motion.button>;
  }
  // Empty cart → urgency CTA with live countdown
  return <motion.a href="#kits" whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.97
  }} animate={{
    boxShadow: ['0 0 0 0 rgba(220,38,38,0.45)', '0 0 0 14px rgba(220,38,38,0)', '0 0 0 0 rgba(220,38,38,0)']
  }} transition={{
    duration: 1.8,
    repeat: Infinity
  }} className="relative group bg-ink text-white px-4 sm:px-6 py-2.5 rounded-full hover:bg-ink-light transition-all flex items-center gap-2 sm:gap-3 min-h-[48px] shadow-premium w-full lg:w-auto justify-center lg:justify-start" data-id="element-659">
      <motion.div animate={{
      rotate: [0, 12, -12, 0],
      scale: [1, 1.15, 1]
    }} transition={{
      duration: 1.4,
      repeat: Infinity
    }} className="flex-shrink-0" data-id="element-660">
        <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" data-id="element-661" />
      </motion.div>
      <div className="flex flex-col items-start leading-tight min-w-0" data-id="element-662">
        <span className="text-[11px] sm:text-xs font-extrabold uppercase tracking-wider" data-id="element-663">
          🔥 Oferta solo por hoy · 50% OFF
        </span>
        <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-bold tabular-nums text-yellow-200" data-id="element-664">
          <span className="whitespace-nowrap" data-id="element-665">Termina en</span>
          <span className="bg-black/30 px-1.5 py-0.5 rounded whitespace-nowrap" data-id="element-666">
            {hours}:{minutes}:{seconds}
          </span>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" data-id="element-667" />
    </motion.a>;
}
export { BrandLogo };